import React, { useEffect, useState } from 'react';

import { useDropzone } from 'react-dropzone'

import {
    Box,
    Typography,
    Button,
    Dialog,
    IconButton,
    Container,
    LinearProgress,
    AppBar,
    Toolbar,
    Grid,
    Paper,
    styled,
    Card,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    ListSubheader,
    ListItem,
    List,
    TextField,
    ListItemText
} from '@mui/material';

import * as yup from "yup";
import { useFormik } from 'formik';

import { grey } from '@mui/material/colors';

import { CommonIcons, icons } from "../../common/CommonIcons"
import CommonSectionHeader from '../../common/CommonSectionHeader';
import CommonRestricted from '../../common/CommonRestricted';

import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useItems, itemTypes, targetTypes, actionTypes } from '../../../hooks/api/useItems';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { useAppContextFeatures } from '../../../context/AppContext';

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));


const RenderFormSavedItem = (props) => {

    const {
        id,
        // parentId,
        index,
        data: file,
        handleSave,
        handleDelete
    } = props

    const preview = file?.file_url;// URL.createObjectURL(file)
    const desc = file?.description? file.description : ""

    const validationSchema = yup.object({
        description: yup
            .string()
            .required("Required"),
    })

    const initialValues = {
        description: desc,
        id,
        // parentId
    }

    const onSubmit = async (currentValues) => {
        await handleSave(currentValues)
    }

    const handleDeleteItem = async (currentValues) => {
        await handleDelete(currentValues)
    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;  

    return (
        <form onSubmit={handleSubmit}>
            <ListItem
                key={index}
                divider
                disablePadding
                disableGutters
                sx={{
                    py: 2,
                }}
            >
                {/* {JSON.stringify(file)} */}
                {/* {JSON.stringify(values)} */}
                {/* {JSON.stringify(errors)} */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={4}>
                        <Box
                            sx={{
                                maxHeight: 200,
                                width: '100%',
                                overflow: 'hidden',
                                borderColor: grey[400],
                                borderWidth: 1,
                                borderStyle: 'solid',
                                borderRadius: 2
                            }}
                        >
                            <img
                                srcSet={`${preview}`}
                                src={`${preview}`}
                                alt={`Preview #${index}`}
                                loading="lazy"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                        <TextField
                            margin="dense"
                            size="small"
                            // autoFocus
                            fullWidth
                            name={"description"}
                            label="Description"
                            type="text" 
                            rows={4}
                            multiline                                        
                            variant="outlined"
                            value={values?.description? values.description : ""}
                            onChange={(event) => {
                                setFieldValue(`description`, event.target.value)
                            }}
                            error={touched.description && Boolean(errors.description)}
                            helperText={touched.description && errors.description}
                        />
                        <Grid item md={12} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                            <Button
                                variant='contained'
                                type='submit'
                                fullWidth
                            >
                                Save Changes
                            </Button>
                            <Button
                                variant='contained'
                                type='button'
                                color={"error"}
                                sx={{ ml: 1 }}
                                onClick={() => {
                                    handleDeleteItem(values)
                                }}
                            >
                                Delete
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </ListItem>
        </form>
    )
}

export default function UpdateItemImages(
    { 
        routeType,
        id,
        handleOnCancel,
        handleOnSelect,
        textProps
    }
) {

    const { promptMessage } = useSystemPrompt()
    const { upload, get, update, remove, isUploading, uploadState } = useItems({ 
        itemType: itemTypes.item, 
        targetType: targetTypes.item, 
        actionType: actionTypes.item.image
    });  

    const { setSystemLoading } = useAppContextFeatures()
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.item.id)

    // const [ uploadCount, setUploadCount ] = useState(0)
    const [ dataSaved, setDataSaved ] = useState([])
  
    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
    } = useDropzone({
        accept: {
            'image/jpeg': [],
            'image/png': []
        }
    });

    const loadData = async (id) => {

        setSystemLoading(true);

        try{

            const { data } = await get({ id, itemType: itemTypes.item })

            const { 
                files,
            } = data;

            setDataSaved(files)

        }catch(error){
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const handleSave = async (currentValues) => {

        setSystemLoading(true)

        try{

            const data = {
                file: {
                    description: currentValues?.description
                }
            }
    
            const response = await update({ id: currentValues?.id, data });
    
            promptMessage(true, `${response?.message}`)
            // navigate(getRoute("products"))

            // if(onSuccessSave) onSuccessSave()

            loadData(id)

        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const handleDelete = async (currentValues) => {

        setSystemLoading(true)

        try{

            const ids = [currentValues?.id]

            const response = await remove({ ids, actionType: actionTypes.item.image });
    
            promptMessage(true, `${response?.message}`)

            loadData(id)

            // if(onSuccessDelete) onSuccessDelete()

        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        loadData(id)

    }, [])

    useEffect(() => {

        handleFileUpload()

    }, [acceptedFiles])

    const handleFileUpload = async () => {

        if(acceptedFiles && acceptedFiles.length <= 0) return false;

        // console.log('acceptedFiles', acceptedFiles)

        try{

            const payloadFiles = acceptedFiles.map((file, index) => {
                return {
                    file,
                    referId: id,
                    fileId: "",
                    description: ``// `Description #${index}`
                }
            });

            let response = await upload({ id, payloadFiles });
            // console.log('response', response)

            promptMessage(true, `${response?.message}`)

            loadData(id)

        }catch(error){
            promptMessage(false, `${error.message}`)
            return false
        }

        return true

    };

    const RenderLinearProgress = () => {

        if(isUploading === false) return <></>

        // console.log('uploadState', uploadState)

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress variant="determinate" value={uploadState?.percent? parseFloat(uploadState.percent) : 0} />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">
                        {`${Math.round(uploadState?.percent,)}%`}
                    </Typography>
                </Box>
            </Box>
        );
    }

    const RenderFormUpload = () => {
        return (
            <Box
                sx={{
                    // border: '3px dashed #eeeeee',
                    borderWidth: 1,
                    borderStyle: 'dotted',
                    borderColor: "primary", // grey[400],
                    backgroundColor: grey[100],
                    borderRadius: 2,
                    // p: 6,
                    py: 3,
                    px: 3,
                    // minHeight: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
                
            >
                <RenderLinearProgress />

                {
                    isUploading === false &&
                    <Box 
                        {...getRootProps({className: 'dropzone'})} 
                        textAlign={"center"}
                        sx={{
                            // p: 2
                        }}
                    >
                        <Button
                            variant='outlined'
                            color="primary"
                        >
                            <input {...getInputProps()} />
                            {/* <Typography
                                sx={{
                                    color: 'secondary.main'
                                }}
                            > */}
                                Drop images here, or click to select files for Upload
                            {/* </Typography> */}
                        </Button>
                    </Box>
                }
{/* 
                <ImageList sx={{ mt: 1 }} cols={10} rowHeight={100}>
                    
                    {
                        acceptedFiles.map((file, index) => {

                            const preview = URL.createObjectURL(file)

                            return (
                                
                                <ImageListItem 
                                    key={index}
                                    sx={{
                                        // borderWidth: 1,
                                        // borderColor: '#e1e1e1',
                                        // borderStyle: 'solid',
                                        // borderRadius: 2,
                                        p: 0
                                    }}
                                >
                                    <img
                                        srcSet={`${preview}`}
                                        src={`${preview}`}
                                        alt={`Preview #${index}`}
                                        loading="lazy"
                                    />
                                </ImageListItem>
                            )

                        })
                    }
                </ImageList> */}

                {/* <Button
                    fullWidth
                    variant='contained'
                    onClick={handleFileUpload}
                    sx={{
                        mt: 1
                    }}
                >
                    Upload
                </Button> */}

            </Box>
        )
    }

    const RenderFormSaved = () => {

        // if(dataSaved.length <= 0){
        //     return (
        //         <ListItemText
        //             primary={"No Images Saved."}
        //             sx={{
        //                 textAlign: 'center'
        //             }}
        //         />
        //     )
        // }

        if(!dataSaved){
            return (
                <ListItemText
                    primary={"No Images Saved."}
                    sx={{
                        textAlign: 'center'
                    }}
                />
            )
        }

        return (
            <List
                disablePadding
            >
                {
                    dataSaved.map((file, index) => {

                        return (
                            // RenderFormSavedItem({
                            //     key: index,
                            //     index,
                            //     data: file
                            // })
                        
                            <RenderFormSavedItem
                                id={file?.id}
                                // parentId={id}
                                key={index}
                                index={index}
                                data={file}
                                handleSave={handleSave}
                                handleDelete={handleDelete}
                            />
                        
                        )
                    })
                }        
            </List>
        )
    }

    const RenderForm = () => {
        return (
            <StyledCard>
                {/* {JSON.stringify(fileRejections)} */}
                {/* {JSON.stringify(acceptedFiles)} */}
                {/* {id} */}
                <Grid container spacing={2}>
                    <Grid item md={12}>
                        <RenderFormUpload />
                    </Grid>
                    <Grid item md={12}>
                        <CommonSectionHeader
                            title={`Saved Images`}
                            subTitle={`Described the image information`}
                        /> 
                        <RenderFormSaved />
                    </Grid>
                </Grid>

                {/* <ImageList sx={{ mt: 1 }} cols={4} rowHeight={200}>
                    {
                        acceptedFiles.map((file, index) => {

                            const preview = URL.createObjectURL(file)

                            return (
                                
                                <ImageListItem 
                                    key={index}
                                    sx={{
                                        borderWidth: 1,
                                        borderColor: '#e1e1e1',
                                        borderStyle: 'solid',
                                        borderRadius: 2,
                                        p: 1
                                    }}
                                >
                                    <img
                                        srcSet={`${preview}`}
                                        src={`${preview}`}
                                        alt={`Preview #${index}`}
                                        loading="lazy"
                                    />
                                    <ImageListItemBar
                                        title={file.path}
                                        subtitle={file.size}
                                        // actionIcon={
                                        // <IconButton
                                        //     sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                        //     aria-label={`info about ${item.title}`}
                                        // >
                                        //     <InfoIcon />
                                        // </IconButton>
                                        // }
                                    />
                                </ImageListItem>
                                // <Grid item md={3}>
                                //     <Paper variant="outlined" sx={{ p: 1, maxHeight: 200 }}>
                                //         <img
                                //             src={preview}
                                            
                                //             // style={{
                                //             //     width: 300,
                                //             //     height: 300
                                //             // }}
                                //             // Revoke data uri after image is loaded
                                //             onLoad={() => { URL.revokeObjectURL(preview) }}
                                //             alt={`Preview #${index}`}
                                //             sx={{
                                //                 objectF
                                //             }}
                                //         />
                                //         <Typography
                                //             sx={{
                                //                 fontSize: 12
                                //             }}
                                //         >
                                //             {file.path}
                                //         </Typography>
                                //     </Paper>
                                // </Grid>
                            )

                        })
                    }
                </ImageList> */}
                
                    {/* <aside>
                        <h4>Files</h4>
                        <ul>{selectedFiles}</ul>
                    </aside> */}

                {/* <Stack direction="row" alignItems="center" spacing={2}>
                    <label htmlFor="upload-image">
                    <Button variant="contained" component="span">
                        Upload
                    </Button>
                    <input
                        id="upload-image"
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handleFileUpload}
                    />
                    </label>
                    {imageUrl && <img src={imageUrl} alt="Uploaded Image" height="300" />}
                </Stack> */}
            </StyledCard>
        )
    }

    return (
        <Box>

            {
                isPermissionAllow({ functionType: functionTypes.pageImages.id })?
                   <RenderForm />
                    :
                    <CommonRestricted />
            }

        </Box>

    )
}